import type { MailAttachment } from "@/lib/types/mail";

export type AttachmentPreviewKind = "pdf" | "image" | "text" | "office";

const OFFICE_EXT_RE = /\.(docx?|xlsx?|xlsm|pptx?|odt|ods|odp|rtf)$/i;
const OFFICE_MIME_RE =
  /^(application\/msword|application\/vnd\.ms-excel|application\/vnd\.ms-powerpoint|application\/rtf|text\/rtf|application\/vnd\.openxmlformats-officedocument\.(wordprocessingml\.document|spreadsheetml\.sheet|presentationml\.presentation)|application\/vnd\.ms-excel\.sheet\.macroenabled\.12|application\/vnd\.oasis\.opendocument\.(text|spreadsheet|presentation))$/i;

/** Détecte une pièce jointe PDF (mime ou extension). */
export function isPdfAttachment(file: MailAttachment): boolean {
  const mime = (file.mime ?? "").toLowerCase();
  const name = (file.filename ?? "").toLowerCase();
  return mime.includes("pdf") || name.endsWith(".pdf");
}

/** Détecte une image (png, jpg, gif, webp…). */
export function isImageAttachment(file: MailAttachment): boolean {
  const mime = (file.mime ?? "").toLowerCase();
  const name = (file.filename ?? "").toLowerCase();
  if (mime.startsWith("image/")) return true;
  return /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif|tiff?)$/i.test(name);
}

/** Texte brut (txt, csv, md, json…) — pas doc/xlsx. */
export function isTextAttachment(file: MailAttachment): boolean {
  const mime = (file.mime ?? "").toLowerCase();
  const name = (file.filename ?? "").toLowerCase();
  if (mime === "text/html") return false;
  if (mime.startsWith("text/")) return true;
  return /\.(txt|csv|md|markdown|log|ics|json|xml|yml|yaml)$/i.test(name);
}

/** Invitation calendrier (.ics ou text/calendar). */
export function isCalendarInviteAttachment(file: MailAttachment): boolean {
  const name = (file.filename ?? "").toLowerCase();
  const mime = (file.mime ?? "").toLowerCase().split(";")[0]?.trim() ?? "";
  if (name.endsWith(".ics")) return true;
  return mime === "text/calendar" || mime === "application/ics" || mime === "text/x-vcalendar";
}

/** Word / Excel / PowerPoint / OpenDocument — aperçu OnlyOffice lecture seule. */
export function isOfficeAttachment(file: MailAttachment): boolean {
  const mime = (file.mime ?? "").toLowerCase();
  const name = (file.filename ?? "").toLowerCase();
  if (OFFICE_EXT_RE.test(name)) return true;
  return OFFICE_MIME_RE.test(mime);
}

export function getAttachmentPreviewKind(file: MailAttachment): AttachmentPreviewKind | null {
  if (isPdfAttachment(file)) return "pdf";
  if (isImageAttachment(file)) return "image";
  if (isTextAttachment(file)) return "text";
  if (isOfficeAttachment(file)) return "office";
  return null;
}

export function isPreviewableAttachment(file: MailAttachment): boolean {
  return getAttachmentPreviewKind(file) != null;
}

/** Première PJ affichable en aperçu (PDF, image, texte, Office). */
export function firstPreviewableAttachment(files: MailAttachment[]): MailAttachment | null {
  return files.find(isPreviewableAttachment) ?? null;
}
